import { DEFAULT_LOCATION } from '@/constants/location'
import { SRP_LISTING_LIMIT } from '@/constants/pageSettings'
import { DEFAULT_SORTING_KEY } from '@/constants/search'
import { type GetSearchResultsPageByUrlQuery } from '@/generated'
import { type GlobalLocation } from '@/lib/apollo/locationCache'
import { type SearchPageProps } from '@/pages/srp/[...categoryOrLocation]'

import { getSearchCategoryFromSearchQuery } from '../category/getSearchCategoryFromSearchQuery'
import {
  getDateFromAppliedFilters,
  getDateRangeFromAppliedFilters,
  getRangeFromAppliedFilters,
  getTreeFromFilters,
} from '../filters'
import { type RouteLocale } from '../locale'
import { getLocationFromSearchQuery } from '../location/getLocationFromSearchQuery'
import { mapSelectedSortOptionToSortKey } from '../srpSorting'
import { getAttributeFromAppliedFilters } from './getAttributeFromAppliedFilters'
import { getSrpContentfulData } from './getSrpContentfulData'
import { type DecodedUrl } from './urlDecode'

export const getFallbackErrorSearchPageProps = (): Omit<SearchPageProps, 'pageLoadId'> => {
  /** Should set global var from URL categoryId on first render */
  const { category } = getSearchCategoryFromSearchQuery(null)
  /** TODO: Figure out grabbing location from decoded url NOT cookie location */
  const location = DEFAULT_LOCATION

  const sortKey = DEFAULT_SORTING_KEY
  return {
    appliedFilters: [],
    category: category,
    categoryPath: [],
    currentPage: 1,
    filters: [],
    errors: {
      hasServerError: true,
    },
    keywords: '',
    limit: SRP_LISTING_LIMIT,
    listings: [],
    location: location,
    searchString: '',
    sortKey,
    sorting: [],
    textJson: null,
    topAds: [],
    totalListings: 0,
    categoryTree: null,
    locationTree: null,
    attributeFilters: [],
    rangeFilters: [],
    dateRangeFilters: [],
    dateFilters: [],
  }
}

export const getSearchPagePropsFromQuery = (
  url: URL,
  decodedUrl: DecodedUrl,
  locale: RouteLocale,
  data: GetSearchResultsPageByUrlQuery['searchResultsPageByUrl']
): Omit<SearchPageProps, 'pageLoadId'> => {
  const { searchQuery: originalSearchQuery, pagination, controls, results } = data

  let searchQuery = originalSearchQuery
  /**
   * If there was a "/", backend will have searched for words delimited by "."
   * So in that case only, replace the returned keyword with keyword that contains "/".
   */
  // TODO: Why this??
  if (decodedUrl.keywords?.includes('/')) {
    searchQuery = { ...searchQuery, keywords: decodedUrl.keywords }
  }

  const { category } = getSearchCategoryFromSearchQuery(searchQuery.category)

  /** Get list of filters and sorting options for current category */
  const { filtering, sorting } = controls

  /** Initial applied filters */
  const appliedFilters = searchQuery.filters

  /** Should set global var from URL locationId on first render */
  const location: GlobalLocation = getLocationFromSearchQuery(searchQuery)

  /** Get listing specific values */
  const organicListings = results?.organic || []
  const topAdListings = results?.topAds || []

  const { limit, offset, totalCount } = pagination
  /**
   * If limit and offset are equal to 0 - current page will be returned as NaN.
   * This will prevent that behaviour and return "page 1" as the current page.
   */
  const currentPage = (offset === 0 && limit === 0 ? 0 : offset / limit) + 1

  /**
   * Redirect user if they are trying to reach a pagination that doesn't exist
   * If total listings is less than the current pagination would need
   */
  // TODO: Deal with this client-side!
  // const redirectReturn = redirectSrp({ currentPage, limit, totalCount, url })
  // if (redirectReturn) return redirectReturn

  /** Get current sort key, can't rely on default to dateDesc as not all categories may have that as default */
  const sortKey = mapSelectedSortOptionToSortKey(
    sorting.find((sortOption: { isSelected: boolean }) => sortOption.isSelected)
  )

  const searchString = searchQuery.searchString

  /* Contentful Data, should be updated / removed when grabbing real data */
  const srpContentfulData = getSrpContentfulData({ languageKey: locale, url })

  const { categoryTree, locationTree } = getTreeFromFilters(filtering)

  const attributeFilters = getAttributeFromAppliedFilters(appliedFilters)
  const rangeFilters = getRangeFromAppliedFilters(appliedFilters)
  const dateRangeFilters = getDateRangeFromAppliedFilters(appliedFilters)
  const dateFilters = getDateFromAppliedFilters(appliedFilters)

  return {
    appliedFilters,
    category,
    categoryPath: category.path ?? [],
    currentPage,
    filters: filtering,
    keywords: searchQuery.keywords || '',
    limit,
    listings: organicListings || [],
    location: location,
    searchString: searchString,
    sortKey,
    sorting,
    textJson: srpContentfulData || null,
    topAds: topAdListings || [],
    totalListings: totalCount,
    categoryTree,
    locationTree,
    attributeFilters,
    rangeFilters,
    dateRangeFilters,
    dateFilters,
  }
}
